import React, { useState } from 'react';
import { 
  CheckCircle, 
  Layout, 
  List, 
  Calendar, 
  BarChart2, 
  Zap, 
  Plus, 
  MoreHorizontal,
  Filter,
  Briefcase,
  Users,
  ArrowRightCircle,
  Trash2,
  CornerDownRight,
  ChevronDown,
  ChevronRight,
  GripVertical,
  Folder
} from 'lucide-react';
import CRMDetailView from './CRMDetailView';

// --- 1. NÚCLEO HÍBRIDO (Datos Simulados) ---
// Combina estructura rígida (id, title) con flexible (custom_fields)
const projectTasks = [
  { 
    id: 1, 
    projectId: 'p1',
    title: "Diseño de Arquitectura", 
    status: "Listo", 
    priority: "Alta", 
    timeline: "2023-10-10", 
    assignee: "Ana G.",
    subtasks: [
      { id: 11, title: "Definir esquema de base de datos", status: "Listo" },
      { id: 12, title: "Diagrama de flujo de datos", status: "Listo" }
    ]
  },
  { 
    id: 2, 
    projectId: 'p1',
    title: "Desarrollo API", 
    status: "En Proceso", 
    priority: "Media", 
    timeline: "2023-10-15", 
    assignee: "Carlos R.",
    subtasks: [
      { id: 21, title: "Endpoint de autenticación", status: "Listo" },
      { id: 22, title: "Endpoint de usuarios", status: "En Proceso" }
    ]
  },
];

const crmLeads = [
  { id: 101, title: "TechCorp - Licencia Enterprise", status: "Negociación", value: "$12,000", contact: "Juan P." },
  { id: 102, title: "StartUp Inc - Plan Pro", status: "Nuevo", value: "$900", contact: "Maria L." },
];

const statusOptions = ['Por Hacer', 'En Proceso', 'Listo', 'Atascado'];
const priorityOptions = ['Baja', 'Media', 'Alta', 'Crítica'];

// --- COMPONENTES UI ---
const Badge = ({ children, color }) => {
  const colors = {
    green: "bg-green-100 text-green-800",
    blue: "bg-blue-100 text-blue-800",
    red: "bg-red-100 text-red-800",
    gray: "bg-gray-100 text-gray-800",
    purple: "bg-purple-100 text-purple-800"
  };
  return <span className={`px-2 py-1 rounded-full text-xs font-semibold ${colors[color] || colors.gray}`}>{children}</span>;
};

const WorkPlatform = () => {
  // Estado para el diferenciador (Principiante vs Experto)
  const [complexityMode, setComplexityMode] = useState('beginner'); // 'beginner' | 'expert'
  const [activeModule, setActiveModule] = useState('projects'); // 'projects' | 'crm'
  const [currentView, setCurrentView] = useState('list'); 
  const [showAutomation, setShowAutomation] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null); // Estado para el lead seleccionado
  const [tasks, setTasks] = useState(projectTasks);
  const [newTask, setNewTask] = useState({ title: '', status: 'Por Hacer', priority: 'Media', assignee: 'Yo', timeline: '' });
  const [newSubtaskTitles, setNewSubtaskTitles] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});
  const [draggedItemIndex, setDraggedItemIndex] = useState(null);
  
  // Estados para gestión de múltiples proyectos
  const [projects, setProjects] = useState([
    { id: 'p1', name: 'Proyecto Alpha', color: 'text-indigo-600' },
    { id: 'p2', name: 'Website Redesign', color: 'text-pink-600' }
  ]);
  const [activeProjectId, setActiveProjectId] = useState('p1');
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  const isExpert = complexityMode === 'expert';
  
  // Estadísticas de tareas
  const filteredTasks = tasks.filter(t => t.projectId === activeProjectId);
  const pendingTasks = filteredTasks.filter(t => t.status !== 'Listo').length;
  const totalTasks = filteredTasks.length;
  const progress = totalTasks === 0 ? 0 : Math.round(((totalTasks - pendingTasks) / totalTasks) * 100);

  const getTodayDateString = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  // Helper para el color de la fecha
  const getDateColor = (dateString) => {
    if (!dateString) return 'text-gray-600';
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);

    if (date < today) return 'text-red-600 font-medium'; // Vencida
    if (date.getTime() === today.getTime()) return 'text-yellow-600 font-medium'; // Hoy
    return 'text-gray-600'; // Futura
  };

  const handleAddTask = () => {
    if (!newTask.title.trim()) return;
    const taskToAdd = { 
      id: Date.now(), 
      projectId: activeProjectId, 
      title: newTask.title, 
      status: newTask.status, 
      priority: newTask.priority, 
      timeline: newTask.timeline || getTodayDateString(), 
      assignee: newTask.assignee, 
      subtasks: [] 
    };
    setTasks([...tasks, taskToAdd]);
    setNewTask({ title: '', status: 'Por Hacer', priority: 'Media', assignee: 'Yo', timeline: '' });
  };

  // --- RENDERIZADORES ---

  // Drag and Drop Handlers
  const handleDragStart = (index) => {
    setDraggedItemIndex(index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (index) => {
    if (draggedItemIndex === null || draggedItemIndex === index) return;
    const newTasks = [...tasks];
    const draggedItem = newTasks[draggedItemIndex];
    newTasks.splice(draggedItemIndex, 1);
    newTasks.splice(index, 0, draggedItem);
    setTasks(newTasks);
    setDraggedItemIndex(null);
  };

  // 1. Módulo de Proyectos (Gestión de Tareas)
  const renderProjectModule = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header Tabla */}
      <div className="flex border-b border-gray-200 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        <div className="p-4 w-10"><input type="checkbox" className="rounded" /></div>
        <div className="p-4 flex-1">Tarea</div>
        <div className="p-4 w-32 text-center">Estado</div>
        <div className="p-4 w-32">Responsable</div>
        <div className="p-4 w-32">Prioridad</div>
        <div className="p-4 w-32">Fecha</div>
        <div className="p-4 w-10"></div>
      </div>

      {/* Lista de Tareas */}
      <div className="divide-y divide-gray-100">
        {filteredTasks.map((task, index) => {
          const isCompleted = task.status === 'Listo';
          const isExpanded = expandedTasks[task.id];
          return (
          <React.Fragment key={task.id}>
          <div 
            key={task.id} 
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(index)}
            className={`flex items-center hover:bg-gray-50 transition-colors group h-16 ${draggedItemIndex === index ? 'opacity-50 bg-gray-100' : ''}`}
          >
            <div className="px-2 w-10 flex items-center justify-center">
              <GripVertical size={14} className="text-gray-300 mr-1 cursor-grab active:cursor-grabbing hover:text-indigo-500" />
              <button onClick={() => setTasks(tasks.map(t => t.id === task.id ? { ...t, status: isCompleted ? 'En Proceso' : 'Listo' } : t))}>
                <CheckCircle className={`cursor-pointer w-5 h-5 transition-colors ${isCompleted ? 'text-green-500 fill-green-50' : 'text-gray-300 hover:text-green-500'}`} />
              </button>
            </div>
            <div className={`p-4 flex-1 font-medium flex items-center transition-all ${isCompleted ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
              <button 
                onClick={() => setExpandedTasks(prev => ({ ...prev, [task.id]: !prev[task.id] }))}
                className="mr-2 text-gray-400 hover:text-indigo-600 transition-colors"
              >
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              {task.title}
              {isExpert && <span className="ml-2 text-[10px] text-gray-400">#{task.id}</span>}
            </div>
            
            {/* Columnas Variables según Modo */}
            <div className={`p-4 w-32 flex justify-center ${isExpert ? 'border-l border-gray-100' : ''}`}>
              <select
                value={task.status}
                onChange={(e) => setTasks(tasks.map(t => t.id === task.id ? { ...t, status: e.target.value } : t))}
                className={`bg-transparent text-xs font-semibold focus:outline-none border-none focus:ring-0 cursor-pointer rounded-full px-2 py-1 ${
                    task.status === "Listo" ? "bg-green-100 text-green-800" : 
                    task.status === "En Proceso" ? "bg-blue-100 text-blue-800" :
                    task.status === "Atascado" ? "bg-red-100 text-red-800" :
                    "bg-gray-100 text-gray-800"
                }`}
              >
                {statusOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className={`p-4 w-32 flex items-center ${isExpert ? 'border-l border-gray-100' : ''}`}>
              <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold mr-2">
                {task.assignee.charAt(0)}
              </div>
              <input
                type="text"
                value={task.assignee}
                onChange={(e) => setTasks(tasks.map(t => t.id === task.id ? { ...t, assignee: e.target.value } : t))}
                className="bg-transparent border-none p-0 text-sm text-gray-600 focus:ring-0 w-full truncate"
              />
            </div>

            <div className={`p-4 w-32 ${isExpert ? 'border-l border-gray-100' : ''}`}>
              <select
                value={task.priority}
                onChange={(e) => setTasks(tasks.map(t => t.id === task.id ? { ...t, priority: e.target.value } : t))}
                className={`bg-transparent text-xs font-semibold focus:outline-none border-none focus:ring-0 cursor-pointer rounded-full px-2 py-1 ${
                    task.priority === "Alta" || task.priority === "Crítica" ? "bg-red-100 text-red-800" : 
                    task.priority === "Media" ? "bg-blue-100 text-blue-800" :
                    "bg-gray-100 text-gray-800"
                }`}
              >
                {priorityOptions.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div className={`p-4 w-32 ${isExpert ? 'border-l border-gray-100' : ''} text-sm text-gray-500`}>
              <input 
                type="date" 
                value={task.timeline}
                onChange={(e) => setTasks(tasks.map(t => t.id === task.id ? { ...t, timeline: e.target.value } : t))}
                className="bg-transparent border-none p-0 text-sm text-gray-600 focus:ring-0 w-full cursor-pointer"
              />
            </div>
            <div className="p-4 w-10 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <button 
                onClick={() => setTasks(tasks.filter(t => t.id !== task.id))}
                className="text-gray-400 hover:text-red-500 transition-colors"
                title="Eliminar tarea"
              >
                <Trash2 size={18} />
              </button>
            </div>
          </div>
          {/* Renderizado de Subtareas */}
          {isExpanded && task.subtasks && task.subtasks.map(subtask => (
            <div key={subtask.id} className="flex items-center bg-gray-50/40 hover:bg-gray-100 transition-colors h-10 group/sub">
              <div className="p-4 w-10"></div> {/* Espacio para checkbox alineado */}
              <div className="p-4 flex-1 flex items-center pl-12 text-sm text-gray-500">
                <CornerDownRight size={14} className="mr-2 text-gray-400" />
                <span className={subtask.status === 'Listo' ? 'line-through decoration-gray-300' : ''}>
                  {subtask.title}
                </span>
              </div>
              
              {/* Columnas alineadas (simplificadas para subtareas) */}
              <div className="p-4 w-32 flex justify-center">
                 <button 
                   onClick={() => {
                     const updatedSubtasks = task.subtasks.map(st => 
                       st.id === subtask.id ? { ...st, status: st.status === 'Listo' ? 'En Proceso' : 'Listo' } : st
                     );
                     
                     // Automatización: Verificar si todas las subtareas están listas
                     const allSubtasksDone = updatedSubtasks.every(st => st.status === 'Listo');
                     let newParentStatus = task.status;
                     
                     if (allSubtasksDone) newParentStatus = 'Listo';
                     else if (task.status === 'Listo') newParentStatus = 'En Proceso';

                     setTasks(tasks.map(t => t.id === task.id ? { ...t, subtasks: updatedSubtasks, status: newParentStatus } : t));
                   }}
                   className="p-1 hover:bg-gray-200 rounded-full transition-colors focus:outline-none"
                 >
                    <div className={`w-2 h-2 rounded-full transition-colors ${subtask.status === 'Listo' ? 'bg-green-500' : 'bg-gray-300'}`} title={subtask.status}></div>
                 </button>
              </div>
              <div className="p-4 w-32"></div> {/* Espacio Asignado */}
              {isExpert && (
                <>
                  <div className="p-4 w-32"></div>
                  <div className="p-4 w-32"></div>
                </>
              )}
              <div className="p-4 w-10"></div>
            </div>
          ))}

          {/* Input para nueva subtarea */}
          {isExpanded && (
          <div className="flex items-center bg-gray-50/40 hover:bg-gray-100 transition-colors h-10 pl-12 border-l-4 border-transparent focus-within:border-indigo-500">
             <div className="p-4 w-10"></div>
             <div className="flex-1 flex items-center pr-4">
                <CornerDownRight size={14} className="mr-2 text-gray-300" />
                <input 
                  type="text"
                  className="bg-transparent border-none focus:ring-0 p-0 w-full text-sm text-gray-600 placeholder-gray-400 focus:outline-none"
                  placeholder="Agregar subtarea..."
                  value={newSubtaskTitles[task.id] || ''}
                  onChange={(e) => setNewSubtaskTitles({ ...newSubtaskTitles, [task.id]: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (newSubtaskTitles[task.id] || '').trim()) {
                      const newSubtask = { 
                        id: Date.now(), 
                        title: newSubtaskTitles[task.id], 
                        status: "En Proceso" 
                      };
                      const updatedTasks = tasks.map(t => t.id === task.id ? { ...t, subtasks: [...(t.subtasks || []), newSubtask] } : t);
                      setTasks(updatedTasks);
                      setNewSubtaskTitles({ ...newSubtaskTitles, [task.id]: '' });
                    }
                  }}
                />
             </div>
          </div>
          )}
          </React.Fragment>
          );
        })}
        
        {/* Fila para agregar nueva tarea con campos completos */}
        <div className="flex items-center hover:bg-gray-50 transition-colors h-16 border-t border-gray-100">
          <div className="p-4 w-10 flex justify-center"><Plus className="w-5 h-5 text-indigo-600" /></div>
          <div className="p-4 flex-1">
            <input 
              type="text"
              className="bg-transparent border-none focus:ring-0 p-0 w-full text-gray-700 placeholder-gray-400 focus:outline-none"
              placeholder="Nueva tarea..."
              value={newTask.title}
              onChange={(e) => setNewTask({...newTask, title: e.target.value})}
              onKeyDown={(e) => e.key === 'Enter' && handleAddTask()}
            />
          </div>
          <div className={`p-4 w-32 flex justify-center ${isExpert ? 'border-l border-gray-100' : ''}`}>
             <select 
                value={newTask.status}
                onChange={(e) => setNewTask({...newTask, status: e.target.value})}
                className="bg-transparent text-xs text-gray-500 focus:outline-none border-none focus:ring-0 cursor-pointer"
             >
                {statusOptions.map(o => <option key={o} value={o}>{o}</option>)}
             </select>
          </div>
          <div className={`p-4 w-32 flex items-center ${isExpert ? 'border-l border-gray-100' : ''}`}>
             <input 
               type="text" 
               value={newTask.assignee}
               onChange={(e) => setNewTask({...newTask, assignee: e.target.value})}
               className="bg-transparent border-none p-0 text-xs text-gray-500 focus:ring-0 w-full placeholder-gray-300"
               placeholder="Responsable"
             />
          </div>
          <div className={`p-4 w-32 flex justify-center ${isExpert ? 'border-l border-gray-100' : ''}`}>
             <select 
                value={newTask.priority}
                onChange={(e) => setNewTask({...newTask, priority: e.target.value})}
                className="bg-transparent text-xs text-gray-500 focus:outline-none border-none focus:ring-0 cursor-pointer"
             >
                {priorityOptions.map(o => <option key={o} value={o}>{o}</option>)}
             </select>
          </div>
          <div className={`p-4 w-32 flex items-center ${isExpert ? 'border-l border-gray-100' : ''}`}>
             <input 
               type="date"
               value={newTask.timeline}
               onChange={(e) => setNewTask({...newTask, timeline: e.target.value})}
               className="bg-transparent border-none p-0 text-xs text-gray-500 focus:ring-0 w-full"
             />
          </div>
          <div className="p-4 w-10 flex justify-center">
             <button onClick={handleAddTask} className="text-gray-400 hover:text-indigo-600 transition-colors" title="Añadir tarea">
               <ArrowRightCircle size={18} />
             </button>
          </div>
        </div>
      </div>
    </div>
  );

  // 3. Vista Kanban
  const renderKanbanBoard = () => {
    const columns = [
      { id: 'Por Hacer', title: 'Por Hacer', color: 'bg-gray-100', border: 'border-gray-200' },
      { id: 'En Proceso', title: 'En Proceso', color: 'bg-blue-50', border: 'border-blue-100' },
      { id: 'Listo', title: 'Listo', color: 'bg-green-50', border: 'border-green-100' }
    ];

    return (
      <div className="flex h-full overflow-x-auto space-x-4 p-2 items-start">
        {columns.map(col => (
          <div key={col.id} className={`flex-shrink-0 w-80 rounded-xl ${col.color} p-4 flex flex-col max-h-full`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-gray-700">{col.title}</h3>
              <span className="bg-white px-2 py-1 rounded text-xs font-bold text-gray-500 shadow-sm">
                {filteredTasks.filter(t => t.status === col.id || (col.id === 'Por Hacer' && !['En Proceso', 'Listo'].includes(t.status))).length}
              </span>
            </div>
            <div className="flex-1 overflow-y-auto space-y-3 min-h-[100px]">
              {filteredTasks
                .filter(t => t.status === col.id || (col.id === 'Por Hacer' && !['En Proceso', 'Listo'].includes(t.status)))
                .map(task => (
                  <div key={task.id} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 cursor-pointer hover:shadow-md transition-shadow group">
                    <div className="flex justify-between items-start mb-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${task.priority === 'Alta' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                        {task.priority}
                      </span>
                      <button className="text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"><MoreHorizontal size={14} /></button>
                    </div>
                    <h4 className="font-medium text-gray-900 mb-2 text-sm">{task.title}</h4>
                    <div className="flex justify-between items-center text-xs text-gray-500">
                      <div className="flex items-center" title={task.assignee}>
                        <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold mr-1 border border-white text-[10px]">
                          {task.assignee.charAt(0)}
                        </div>
                      </div>
                      <div className={getDateColor(task.timeline)}>{task.timeline}</div>
                    </div>
                  </div>
                ))}
            </div>
            <button 
              onClick={() => {
                const newTask = { id: Date.now(), projectId: activeProjectId, title: 'Nueva Tarea', status: col.id, priority: "Media", timeline: getTodayDateString(), assignee: "Yo", subtasks: [] };
                setTasks([...tasks, newTask]);
              }}
              className="mt-3 flex items-center justify-center text-gray-500 hover:text-gray-700 py-2 hover:bg-white/50 rounded-lg transition-colors text-sm font-medium w-full border border-dashed border-gray-300"
            >
              <Plus size={16} className="mr-1" /> Añadir
            </button>
          </div>
        ))}
      </div>
    );
  };

  // 4. Vista Gantt (Simplificada)
  const renderGanttChart = () => {
    const dates = [];
    const today = new Date();
    today.setHours(0,0,0,0);
    // Generar vista de 2 semanas
    for (let i = -2; i < 12; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      dates.push(d);
    }

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-full">
        <div className="flex border-b border-gray-200 bg-gray-50">
          <div className="w-64 p-4 border-r border-gray-200 flex-shrink-0 font-semibold text-xs text-gray-500 uppercase tracking-wider">Tarea</div>
          <div className="flex-1 flex overflow-x-auto hide-scrollbar">
            {dates.map((date, i) => (
              <div key={i} className={`flex-shrink-0 w-12 text-center border-r border-gray-100 py-2 text-xs flex flex-col justify-center ${date.getTime() === today.getTime() ? 'bg-indigo-50 text-indigo-600 font-bold' : 'text-gray-500'}`}>
                <span className="text-[10px]">{['D','L','M','X','J','V','S'][date.getDay()]}</span>
                <span className="text-sm">{date.getDate()}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredTasks.map(task => {
             // Lógica visual simple para demostración: Duración fija de 3 días terminando en fecha de entrega
             const dueDate = new Date(task.timeline);
             // Buscar índice de fecha
             const dateIndex = dates.findIndex(d => d.toDateString() === dueDate.toDateString());
             // Si la fecha está fuera de rango, no mostramos la barra o la mostramos truncada
             const barLeft = dateIndex > -1 ? (dateIndex - 2) * 3 : 0; // -2 para que termine en la fecha (3rem ancho por día)
             const isVisible = dateIndex > -1;

             return (
              <div key={task.id} className="flex border-b border-gray-100 hover:bg-gray-50 group h-12">
                <div className="w-64 px-4 border-r border-gray-200 flex-shrink-0 flex items-center text-sm font-medium text-gray-700 truncate">
                  {task.title}
                </div>
                <div className="flex-1 relative">
                  <div className="absolute inset-0 flex">
                    {dates.map((_, i) => <div key={i} className="w-12 border-r border-gray-50 h-full"></div>)}
                  </div>
                  {isVisible && (
                    <div 
                      className={`absolute top-3 h-6 rounded-md shadow-sm text-[10px] text-white flex items-center px-2 truncate ${task.status === 'Listo' ? 'bg-green-500 opacity-60' : 'bg-indigo-500'}`}
                      style={{ left: `${Math.max(0, barLeft)}rem`, width: '9rem' }} // 3 días * 3rem = 9rem
                      title={`Vence: ${task.timeline}`}
                    >
                      {task.title}
                    </div>
                  )}
                </div>
              </div>
             );
          })}
        </div>
      </div>
    );
  };

  // 2. Módulo CRM (Gestión de Leads)
  const renderCRMModule = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="flex border-b border-gray-200 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        <div className="p-4 flex-1">Oportunidad</div>
        <div className="p-4 w-40">Etapa</div>
        <div className="p-4 w-32">Valor</div>
        {isExpert && <div className="p-4 w-40">Probabilidad</div>}
      </div>
      <div className="divide-y divide-gray-100">
        {crmLeads.map(lead => (
          <div 
            key={lead.id} 
            onClick={() => setSelectedLead(lead)} 
            className="flex items-center hover:bg-gray-50 h-16 cursor-pointer transition-colors"
          >
            <div className="p-4 flex-1 font-medium text-gray-900">{lead.title}</div>
            <div className="p-4 w-40">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full" 
                  style={{ width: lead.status === 'Negociación' ? '75%' : '25%' }}
                ></div>
              </div>
              <span className="text-xs text-gray-500 mt-1 block">{lead.status}</span>
            </div>
            <div className="p-4 w-32 text-sm font-semibold text-gray-700">{lead.value}</div>
            {isExpert && (
              <div className="p-4 w-40 text-sm text-gray-500">
                {lead.status === 'Negociación' ? '80%' : '20%'}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800 flex flex-col">
      
      {/* --- HEADER --- */}
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center sticky top-0 z-20">
        <div className="flex items-center space-x-6">
          <div className="flex items-center space-x-2">
            <div className="bg-indigo-600 p-2 rounded-lg">
              <Layout className="text-white h-5 w-5" />
            </div>
            <span className="font-bold text-lg">Proyect Agent</span>
            {activeModule === 'projects' && (
              // El progreso ahora se muestra en el contexto del proyecto activo
              null
            )}
          </div>
          
          {/* Selector de Módulos (CRM vs Proyectos) */}
          <div className="hidden md:flex bg-gray-100 rounded-lg p-1">
            <button 
              onClick={() => setActiveModule('projects')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeModule === 'projects' ? 'bg-white shadow text-indigo-600' : 'text-gray-500'}`}
            >
              Proyectos
            </button>
            <button 
              onClick={() => setActiveModule('crm')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeModule === 'crm' ? 'bg-white shadow text-purple-600' : 'text-gray-500'}`}
            >
              CRM
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Diferenciador: Switch de Complejidad */}
          <div className="flex items-center space-x-2 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200">
            <span className={`text-xs font-bold ${!isExpert ? 'text-indigo-600' : 'text-gray-400'}`}>Simple</span>
            <button 
              onClick={() => setComplexityMode(isExpert ? 'beginner' : 'expert')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${isExpert ? 'bg-purple-600' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${isExpert ? 'translate-x-4.5' : 'translate-x-1'}`} />
            </button>
            <span className={`text-xs font-bold ${isExpert ? 'text-purple-600' : 'text-gray-400'}`}>Experto</span>
          </div>
          
          <div className="h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-700 font-bold border border-indigo-200">
            JS
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* --- SIDEBAR DE PROYECTOS --- */}
        {activeModule === 'projects' && (
          <div className="w-64 bg-white border-r border-gray-200 flex-shrink-0 flex flex-col">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Mis Proyectos</h2>
              <button 
                onClick={() => setIsCreatingProject(true)}
                className="text-gray-400 hover:text-indigo-600 hover:bg-gray-100 rounded p-1 transition-colors"
                title="Crear Proyecto"
              >
                <Plus size={16} />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {projects.map(p => (
                <button
                  key={p.id}
                  onClick={() => setActiveProjectId(p.id)}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${activeProjectId === p.id ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  <Folder size={16} className={`mr-2 ${p.color}`} />
                  {p.name}
                </button>
              ))}

              {isCreatingProject && (
                <div className="px-3 py-2">
                  <input 
                    autoFocus
                    type="text"
                    className="w-full text-sm border border-indigo-300 rounded px-2 py-1 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                    placeholder="Nombre del proyecto..."
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newProjectName.trim()) {
                        const newProject = { id: Date.now(), name: newProjectName, color: 'text-pink-500' };
                        setProjects([...projects, newProject]);
                        setActiveProjectId(newProject.id);
                        setNewProjectName('');
                        setIsCreatingProject(false);
                      } else if (e.key === 'Escape') {
                        setIsCreatingProject(false);
                      }
                    }}
                    onBlur={() => !newProjectName.trim() && setIsCreatingProject(false)}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* --- CONTENIDO PRINCIPAL (Toolbar + Body) --- */}
        <div className="flex-1 flex flex-col min-w-0 bg-gray-50">
          
          {/* --- BARRA DE HERRAMIENTAS --- */}
          <div className="bg-white border-b border-gray-200 px-6 py-2 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex space-x-1 overflow-x-auto pb-2 md:pb-0">
              {[
                { id: 'list', icon: List, label: 'Lista' },
                { id: 'board', icon: Layout, label: 'Kanban' },
                { id: 'gantt', icon: Calendar, label: 'Gantt' },
                ...(isExpert ? [{ id: 'dash', icon: BarChart2, label: 'Dashboard' }] : [])
              ].map((view) => (
                <button
                  key={view.id}
                  onClick={() => setCurrentView(view.id)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm whitespace-nowrap ${currentView === view.id ? 'bg-indigo-50 text-indigo-600 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  <view.icon size={16} />
                  <span>{view.label}</span>
                </button>
              ))}
            </div>

            <div className="flex items-center space-x-3">
              {activeModule === 'projects' && (
                <div className="hidden lg:flex items-center space-x-2 mr-2" title={`${pendingTasks} pendientes`}>
                  <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-600 transition-all duration-500 ease-out" style={{ width: `${progress}%` }}></div>
                  </div>
                  <span className="text-xs font-medium text-gray-500">{progress}%</span>
                </div>
              )}

              {isExpert && (
                <button 
                  onClick={() => setShowAutomation(!showAutomation)}
                  className="flex items-center space-x-1 text-gray-600 text-sm px-3 py-1.5 hover:bg-gray-100 rounded-md border border-transparent hover:border-gray-200"
                >
                  <Zap size={16} className="text-yellow-500" />
                  <span>Automatizar</span>
                </button>
              )}
              <button className="flex items-center space-x-1 text-gray-600 text-sm px-3 py-1.5 hover:bg-gray-100 rounded-md">
                <Filter size={16} />
                <span>Filtrar</span>
              </button>
              <button className={`text-white px-3 py-1.5 rounded-md text-sm font-medium flex items-center ${activeModule === 'crm' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-indigo-600 hover:bg-indigo-700'}`}>
                <Plus size={16} className="mr-1" /> 
                {activeModule === 'crm' ? 'Nuevo Lead' : 'Nueva Tarea'}
              </button>
            </div>
          </div>

          {/* --- AUTOMATIZACIÓN (Panel Desplegable) --- */}
          {showAutomation && isExpert && (
            <div className="bg-slate-900 text-white px-6 py-4 border-b border-slate-700 animate-in slide-in-from-top-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold flex items-center"><Zap className="mr-2 text-yellow-400" size={18}/> Centro de Automatización</h3>
                <button className="text-xs bg-slate-700 px-2 py-1 rounded">Nueva Regla +</button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-800 p-3 rounded border border-slate-700 text-sm opacity-80 hover:opacity-100 cursor-pointer">
                  <div className="text-slate-400 text-xs mb-1">Trigger: Estado cambia a "Listo"</div>
                  <div className="font-medium">→ Notificar al cliente por Email</div>
                </div>
                <div className="bg-slate-800 p-3 rounded border border-slate-700 text-sm opacity-80 hover:opacity-100 cursor-pointer">
                  <div className="text-slate-400 text-xs mb-1">Trigger: Nueva Tarea creada</div>
                  <div className="font-medium">→ Asignar a PM automáticamente</div>
                </div>
              </div>
            </div>
          )}

          {/* --- ÁREA PRINCIPAL --- */}
          <main className="flex-1 overflow-auto p-6">
            {/* Breadcrumb del módulo activo */}
            <div className="mb-4 flex items-center text-sm text-gray-500">
              {activeModule === 'projects' ? <Briefcase size={16} className="mr-2"/> : <Users size={16} className="mr-2"/>}
              <span className="font-medium text-gray-900 capitalize">{activeModule}</span>
              <span className="mx-2">/</span>
              <span>{activeModule === 'projects' ? projects.find(p => p.id === activeProjectId)?.name : 'Gestión Comercial'}</span>
            </div>

            {/* Renderizado Condicional */}
            {activeModule === 'projects' && currentView === 'list' && renderProjectModule()}
            {activeModule === 'projects' && currentView === 'board' && renderKanbanBoard()}
            {activeModule === 'projects' && currentView === 'gantt' && renderGanttChart()}
            
            {activeModule === 'crm' && currentView === 'list' && renderCRMModule()}

            {((currentView === 'dash') || (activeModule === 'crm' && currentView !== 'list')) && (
              <div className="flex flex-col items-center justify-center h-96 border-2 border-dashed border-gray-300 rounded-xl bg-white bg-opacity-50">
                 <Layout size={48} className="mb-4 text-gray-300" />
                 <h3 className="text-lg font-medium text-gray-500">Vista {currentView === 'board' ? 'Kanban' : 'Dashboard'}</h3>
                 <p className="text-gray-400 text-sm mt-2">Disponible en la versión completa.</p>
              </div>
            )}
          </main>
        </div>
      </div>

      {/* --- VISTA DE DETALLE (SLIDE-OVER) --- */}
      {selectedLead && (
        <CRMDetailView lead={selectedLead} onClose={() => setSelectedLead(null)} />
      )}
      
    </div>
  );
};

export default WorkPlatform;